import { useState } from "react";
import { Check, Copy, Zap } from "lucide-react";
import { euro } from "@/lib/api";
import { T, useLang } from "@/lib/i18n";

const Row = ({ label, value, id, copy }) => {
  const [done, setDone] = useState(false);
  const { t } = useLang();
  const onCopy = () => {
    navigator.clipboard?.writeText(copy ?? value).then(() => {
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    }).catch(() => null);
  };
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[var(--line)] py-3 last:border-0">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.2em] text-[var(--parchment)]/70">{t(label)}</p>
        <p data-testid={`bank-${id}`} className="mt-1 break-all text-base font-semibold tracking-wide tabular-nums text-[var(--parchment)]">{value}</p>
      </div>
      <button type="button" data-testid={`bank-copy-${id}`} onClick={onCopy} aria-label={`${t("Copier")} ${t(label)}`}
        className="shrink-0 rounded-full border border-[var(--line)] p-2 transition-colors hover:border-[var(--gold)]">
        {done ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
};

export const InstantTransferNote = ({ className = "", testid = "instant-transfer-note" }) => (
  <p data-testid={testid} className={`flex gap-3 rounded-sm border border-[var(--gold)]/40 bg-[rgba(217,180,106,0.08)] px-4 py-3 text-left text-sm leading-relaxed text-[var(--parchment)] ${className}`}>
    <Zap className="mt-0.5 h-4 w-4 shrink-0 text-[var(--gold)]" strokeWidth={1.5} aria-hidden="true" />
    <span><T>Pour accélérer le traitement de votre commande, nous vous recommandons d'effectuer un </T><b className="font-semibold"><T>virement instantané</T></b><T>. Votre commande pourra ainsi être traitée plus rapidement dès réception de votre paiement.</T></span>
  </p>
);

export const BankBox = ({ bank, amount, reference, payBefore }) => {
  const { lang } = useLang();
  return (
  <div data-testid="bank-transfer-box" className="mx-auto mt-8 w-full max-w-md border-l-2 border-[var(--gold)] bg-white/5 px-5 py-4 text-left">
    <p className="eyebrow mb-2"><T>Payer par virement</T></p>
    <InstantTransferNote className="mb-2 mt-3" testid="bank-instant-transfer-note" />
    {bank.holder ? <Row label="Titulaire" value={bank.holder} id="holder" /> : null}
    <Row label="IBAN" value={bank.iban} id="iban" copy={bank.iban.replace(/\s/g, "")} />
    {bank.bic ? <Row label="BIC" value={bank.bic} id="bic" /> : null}
    <Row label="Montant" value={euro(amount)} id="amount" copy={String(amount)} />
    <Row label="Référence à indiquer" value={reference} id="reference" />
    {payBefore ? (
      <p data-testid="bank-deadline" className="mt-3 text-xs leading-5 text-[var(--parchment)]/80">
        {lang === "en"
          ? `To be paid before ${new Date(payBefore).toLocaleDateString("en-GB")}. If no transfer is received within 3 days, the reservation is cancelled automatically.`
          : `À régler avant le ${new Date(payBefore).toLocaleDateString("fr-FR")}. Sans virement reçu sous 3 jours, la réservation est automatiquement annulée.`}
      </p>
    ) : null}
  </div>
  );
};
